/**
 * Turn rows into a CSV file and hand it to the browser.
 *
 * One implementation, because the escaping is the part that goes wrong quietly:
 * a note containing a comma silently becomes two columns and shifts every column
 * after it, and a name beginning with "=" is executed by Excel when the file is
 * opened. Both are easy to get right once and easy to forget the second time.
 */

/** A field, quoted where it has to be and guarded against formula injection. */
export function csvCell(value: string | number | null | undefined): string {
  const text = String(value ?? '');
  // Excel and Sheets both treat a leading =, +, - or @ as a formula. An apostrophe
  // makes it literal text without changing what the reader sees.
  const guarded = /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
  return /[",\r\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}

export function toCsv(rows: (string | number | null | undefined)[][]): string {
  return rows.map((row) => row.map(csvCell).join(',')).join('\r\n');
}

/**
 * Download rows as a CSV file.
 *
 * The leading BOM is what makes Excel read the file as UTF-8 rather than the
 * local codepage - without it any non-ASCII name is mangled on open.
 */
export function downloadCsv(filename: string, rows: (string | number | null | undefined)[][]): void {
  const blob = new Blob([`﻿${toCsv(rows)}\r\n`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}
